import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import {
  Print,
  Add as AddIcon,
  MoreVert,
  Close,
  FilterList
} from '@mui/icons-material';
import { customerService } from '../services/customerService';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { procurementSummaryFieldsRowSx } from '../components/procurement/ProcurementSummaryBar';
import { useResizableColumns } from '../hooks/useResizableColumns';
import usePersistedListFilters from '../hooks/usePersistedListFilters';
import { printWithFilename } from '../utils/printUtils';

const CUSTOMER_LIST_HEADER_FILTERS_INITIAL = {
  custNo: '',
  name: '',
  shortName: '',
  phone: '',
  city: '',
  state: '',
  country: '',
  salesRep: '',
  notes: '',
  terms: '',
  workCode: '',
  active: ''
};

const CUSTOMER_LIST_FILTERS_DEFAULTS = {
  statusFilter: 'active',
  headerColFilters: { ...CUSTOMER_LIST_HEADER_FILTERS_INITIAL },
  sortBy: 'customer_number',
  sortOrder: 'asc',
  page: 0
};

function trimHeaderFilterObj(obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '').trim()]));
}

const colHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.375,
  width: '100%',
  pr: 0
};

const colHeaderLabelSlotSx = {
  flexShrink: 0,
  width: '100%',
  minHeight: '1.5rem',
  overflow: 'hidden',
  '& .MuiTableSortLabel-root': {
    maxWidth: '100%',
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.25,
    fontSize: '0.65rem',
    fontWeight: 700,
    '& .MuiTableSortLabel-icon': { flexShrink: 0 }
  }
};

const colHeaderCaptionInSlotSx = {
  fontSize: '0.65rem',
  fontWeight: 700,
  lineHeight: 1.25,
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%'
};

const headerFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1 },
  '& .MuiInputBase-input': { py: 0.35 }
};

const Customers = () => {
  const DEFAULT_COL_WIDTHS = {
    custNo: 120, shortName: 130, name: 220, phone: 140, city: 140, state: 90, country: 110,
    salesRep: 130, notes: 220, terms: 110, workCode: 110, active: 90, actions: 120
  };
  const navigate = useNavigate();
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_customers_list_filters', CUSTOMER_LIST_FILTERS_DEFAULTS);
  const statusFilter = listFilters.statusFilter;
  const setStatusFilter = useCallback((v) => setListFilter('statusFilter', v), [setListFilter]);
  const headerColFilters = listFilters.headerColFilters;
  const setHeaderColFilters = useCallback((v) => setListFilter('headerColFilters', v), [setListFilter]);
  const sortBy = listFilters.sortBy;
  const setSortBy = useCallback((v) => setListFilter('sortBy', v), [setListFilter]);
  const sortOrder = listFilters.sortOrder;
  const setSortOrder = useCallback((v) => setListFilter('sortOrder', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
  }, [clearListFilters]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [debouncedHeaderColFilters, setDebouncedHeaderColFilters] = useState(() =>
    trimHeaderFilterObj(headerColFilters || CUSTOMER_LIST_HEADER_FILTERS_INITIAL)
  );
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const raw = parseInt(window.localStorage.getItem('wm_customers_rows_per_page'), 10);
      return [50, 100, 200].includes(raw) ? raw : 50;
    } catch {
      return 50;
    }
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_customers_col_widths',
    minWidths: {
      custNo: 90,
      shortName: 80,
      name: 140,
      phone: 110,
      city: 100,
      state: 70,
      country: 90,
      salesRep: 100,
      notes: 140,
      terms: 90,
      workCode: 90,
      active: 80,
      actions: 100
    }
  });

  const customerLongName = (c) =>
    c.company_name ||
    (c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.first_name || c.last_name || '');

  const customerShortName = (c) => {
    if (c?.short_name == null || String(c.short_name).trim() === '') return '';
    return String(c.short_name).trim();
  };

  /** Display / search / dialogs: long name, with short prefix when useful outside the table. */
  const custName = (c) => {
    const long = customerLongName(c);
    const sh = customerShortName(c);
    if (sh && long) return `${sh} · ${long}`;
    return sh || long || '-';
  };

  const accountRepName = (c) => {
    if (c?.account_rep_first_name || c?.account_rep_last_name) {
      return `${c.account_rep_first_name || ''} ${c.account_rep_last_name || ''}`.trim();
    }
    return c?.sales_id || '';
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const next = trimHeaderFilterObj(headerColFilters);
      setDebouncedHeaderColFilters((prev) => {
        const same = Object.keys(CUSTOMER_LIST_HEADER_FILTERS_INITIAL).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [headerColFilters]);

  useEffect(() => {
    setPage(0);
  }, [debouncedHeaderColFilters, statusFilter]);

  const filteredAndSortedCustomers = useMemo(() => {
    let list = customers;
    if (statusFilter === 'active') list = list.filter((c) => c.is_active);
    else if (statusFilter === 'inactive') list = list.filter((c) => !c.is_active);

    const f = debouncedHeaderColFilters;
    const includes = (value, needle) =>
      !needle || String(value ?? '').toLowerCase().includes(needle.toLowerCase());

    list = list.filter((c) => {
      const activeLabel = c.is_active ? 'y active' : 'n inactive';
      return (
        includes(c.customer_number ?? c.id, f.custNo) &&
        includes(customerLongName(c), f.name) &&
        includes(customerShortName(c), f.shortName) &&
        includes(c.phone, f.phone) &&
        includes(c.city, f.city) &&
        includes(c.state, f.state) &&
        includes(c.country || 'USA', f.country) &&
        includes(accountRepName(c), f.salesRep) &&
        includes(c.notes, f.notes) &&
        includes(c.terms, f.terms) &&
        includes(c.work_code, f.workCode) &&
        includes(activeLabel, f.active)
      );
    });

    const nameStr = (c) => customerLongName(c) || '-';
    const cmp = (a, b) => {
      let va = a[sortBy];
      let vb = b[sortBy];
      if (sortBy === 'account_rep') {
        va = accountRepName(a);
        vb = accountRepName(b);
      }
      if (sortBy === 'name') {
        va = nameStr(a);
        vb = nameStr(b);
      } else if (sortBy === 'short_name') {
        va = customerShortName(a).toLowerCase() || '\uffff';
        vb = customerShortName(b).toLowerCase() || '\uffff';
      } else if (sortBy === 'is_active') {
        va = a.is_active ? 1 : 0;
        vb = b.is_active ? 1 : 0;
      } else {
        va = String(va ?? '').toLowerCase();
        vb = String(vb ?? '').toLowerCase();
      }
      if (va < vb) return sortOrder === 'asc' ? -1 : 1;
      if (va > vb) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    };
    return [...list].sort(cmp);
  }, [customers, statusFilter, debouncedHeaderColFilters, sortBy, sortOrder]);

  const pagedCustomers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedCustomers.slice(start, start + rowsPerPage);
  }, [filteredAndSortedCustomers, page, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredAndSortedCustomers.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredAndSortedCustomers.length, rowsPerPage, page]);

  const handleSort = (column) => {
    if (sortBy === column) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const renderHeaderFilter = (key) => (
    <TextField
      size="small"
      placeholder="Filter…"
      value={headerColFilters[key]}
      onChange={(e) => setHeaderColFilters((p) => ({ ...p, [key]: e.target.value }))}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      sx={headerFilterTextFieldSx}
    />
  );

  /** Match Orders / Quotes lists — tighter than MUI `size="small"` alone. */
  const customersTableDenseSx = {
    '& .MuiTableCell-root': {
      py: 0.35,
      px: '8px',
      fontSize: '0.75rem',
      lineHeight: 1.2,
      verticalAlign: 'middle'
    },
    '& .MuiTableCell-head': {
      py: 0.45,
      fontSize: '0.7rem',
      verticalAlign: 'top'
    },
    '& .MuiTableCell-head .MuiBox-root': { pr: 1 },
    '& .MuiChip-root': {
      height: 20,
      maxWidth: '100%',
      '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', lineHeight: 1.1 }
    },
    '& .MuiIconButton-root': { p: 0.25 },
    '& .MuiButton-sizeSmall': {
      py: 0.125,
      px: 0.75,
      fontSize: '0.75rem',
      lineHeight: 1.2,
      minHeight: 24
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [statusFilter]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        activeOnly: statusFilter === 'active' ? true : false,
        limit: 500
      };
      const response = await customerService.getAll(params);
      const data = response.data || [];
      setCustomers(data);
      setSelectedCustomer(data.length > 0 ? data[0] : null);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleEditCustomer = (customer) => {
    if (!customer?.id) return;
    navigate(`/customers/${customer.id}`);
  };

  const handleMenuOpen = (event, customer) => {
    event.stopPropagation();
    setSelectedCustomer(customer);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (event, customer) => {
    event.stopPropagation();
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!customerToDelete) return;
    try {
      setDeleting(true);
      setError('');
      await customerService.delete(customerToDelete.id);
      handleDeleteDialogClose();
      await loadCustomers();
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError(err.response?.data?.message || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handlePrint = () => printWithFilename('Customers');

  return (
    <Box>
      <Box className="no-print-customers">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Customers
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/customers/new')}
            sx={{ mr: 0.5 }}
          >
            New
          </Button>
          <IconButton size="small" color="primary" title="Print" onClick={(e) => setPrintMenuAnchor(e.currentTarget)}>
            <Print fontSize="small" />
          </IconButton>
          <Menu anchorEl={printMenuAnchor} open={!!printMenuAnchor} onClose={() => setPrintMenuAnchor(null)} disableEnforceFocus disableRestoreFocus>
            <MenuItem onClick={() => { handlePrint(); setPrintMenuAnchor(null); }}>Print list</MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedCustomer?.id) navigate(`/customers/${selectedCustomer.id}`);
                setPrintMenuAnchor(null);
              }}
              disabled={!selectedCustomer}
            >
              Print selected
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Customer Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main' }}>
        <Box sx={procurementSummaryFieldsRowSx()}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedCustomer?.customer_number ?? selectedCustomer?.id ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer name</Typography>
            <Typography variant="body2">
              {selectedCustomer ? customerLongName(selectedCustomer) || '—' : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Short name</Typography>
            <Typography variant="body2">
              {selectedCustomer ? customerShortName(selectedCustomer) || '—' : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Phone</Typography>
            <Typography variant="body2">
              {selectedCustomer?.phone ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Email</Typography>
            <Typography variant="body2">
              {selectedCustomer?.email ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>City</Typography>
            <Typography variant="body2">
              {selectedCustomer?.city ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>State</Typography>
            <Typography variant="body2">
              {selectedCustomer?.state ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Country</Typography>
            <Typography variant="body2">
              {selectedCustomer?.country ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            <Chip
              label={selectedCustomer?.is_active ? 'Active' : 'Inactive'}
              color={selectedCustomer?.is_active ? 'success' : 'default'}
              size="small"
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedCustomer(null)}
            disabled={!selectedCustomer}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Status filter + result count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        {hasActiveFilters ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            onClick={handleClearFilters}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear filters
          </Button>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {loading
            ? 'Loading…'
            : filteredAndSortedCustomers.length === 0
              ? 'No customers match'
              : `Showing ${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredAndSortedCustomers.length)} of ${filteredAndSortedCustomers.length} customer${filteredAndSortedCustomers.length === 1 ? '' : 's'}`}
        </Typography>
      </Box>

      {/* Customers Table */}
      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader sx={customersTableDenseSx}>
          <TableHead>
            <TableRow>
              {[
                { key: 'custNo', filterKey: 'custNo', sortKey: 'customer_number', label: '#' },
                { key: 'name', filterKey: 'name', sortKey: 'name', label: 'Customer name' },
                { key: 'shortName', filterKey: 'shortName', sortKey: 'short_name', label: 'Short name' },
                { key: 'phone', filterKey: 'phone', sortKey: 'phone', label: 'Phone' },
                { key: 'city', filterKey: 'city', sortKey: 'city', label: 'City' },
                { key: 'state', filterKey: 'state', sortKey: 'state', label: 'State' },
                { key: 'country', filterKey: 'country', sortKey: 'country', label: 'Country' },
                { key: 'salesRep', filterKey: 'salesRep', sortKey: 'account_rep', label: 'Account Rep' },
                { key: 'notes', filterKey: 'notes', sortKey: 'notes', label: 'Comments' },
                { key: 'terms', filterKey: 'terms', sortKey: 'terms', label: 'Payment Terms' },
                { key: 'workCode', filterKey: 'workCode', sortKey: 'work_code', label: 'Work Code' },
                { key: 'active', filterKey: 'active', sortKey: 'is_active', label: 'Active' }
              ].map((col) => (
                <ResizableHeaderCell
                  key={col.key}
                  width={colWidths[col.key]}
                  onResizeStart={(e) => startResize(col.key, e)}
                  sortDirection={sortBy === col.sortKey ? sortOrder : false}
                >
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel
                        active={sortBy === col.sortKey}
                        direction={sortBy === col.sortKey ? sortOrder : 'asc'}
                        onClick={() => handleSort(col.sortKey)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </Box>
                    {renderHeaderFilter(col.filterKey)}
                  </Box>
                </ResizableHeaderCell>
              ))}
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={colHeaderCaptionInSlotSx}>
                      Actions
                    </Typography>
                  </Box>
                </Box>
              </ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  {customers.length === 0 ? 'No customers found. Click "New" to create your first customer.' : 'No customers match your filters.'}
                </TableCell>
              </TableRow>
            ) : (
              pagedCustomers.map((customer) => (
                <TableRow
                  key={customer.id}
                  hover
                  selected={selectedCustomer?.id === customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  onDoubleClick={() => handleCustomerClick(customer.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.custNo }}>{customer.customer_number || customer.id}</TableCell>
                  <TableCell sx={{ width: colWidths.name }}>
                    {customerLongName(customer) || '—'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.shortName }}>
                    {customerShortName(customer) || '—'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.phone }}>{customer.phone || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.city }}>{customer.city || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.state }}>{customer.state || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.country }}>{customer.country || 'USA'}</TableCell>
                  <TableCell sx={{ width: colWidths.salesRep }}>
                    {accountRepName(customer) || '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.notes }}>
                    {customer.notes
                      ? customer.notes.length > 30 
                        ? customer.notes.substring(0, 30) + '...'
                        : customer.notes
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.terms }}>{customer.terms || '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.workCode }}>{customer.work_code || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.active }}>
                    <Chip
                      label={customer.is_active ? 'Y' : 'N'}
                      color={customer.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ width: colWidths.actions }} onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, customer)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredAndSortedCustomers.length}
        page={page}
        onPageChange={(_e, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[50, 100, 200]}
        onRowsPerPageChange={(e) => {
          const next = parseInt(e.target.value, 10);
          setRowsPerPage(next);
          setPage(0);
          try {
            window.localStorage.setItem('wm_customers_rows_per_page', String(next));
          } catch {
            // ignore storage failures
          }
        }}
      />

      </Box>

      {/* Print view – customer list */}
      <PrintPage title="Customers" showSpacer={false}>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {filteredAndSortedCustomers.length} record(s). Printed{' '}
          {new Date().toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
        </p>
        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer name</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Short name</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Phone</th>
              <th style={{ padding: 6, textAlign: 'left' }}>City</th>
              <th style={{ padding: 6, textAlign: 'left' }}>State</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Active</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedCustomers.map((c) => (
              <tr key={c.id}>
                <td style={{ padding: 6 }}>{c.customer_number || c.id}</td>
                <td style={{ padding: 6 }}>{customerLongName(c) || '—'}</td>
                <td style={{ padding: 6 }}>{customerShortName(c) || '—'}</td>
                <td style={{ padding: 6 }}>{c.phone || '-'}</td>
                <td style={{ padding: 6 }}>{c.city || '-'}</td>
                <td style={{ padding: 6 }}>{c.state || '-'}</td>
                <td style={{ padding: 6 }}>{c.is_active ? 'Y' : 'N'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintPage>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedCustomer) handleCustomerClick(selectedCustomer.id);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCustomer) handleEditCustomer(selectedCustomer);
          handleMenuClose();
        }}        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => { handlePrint(); handleMenuClose(); }}>Print</MenuItem>
        <MenuItem onClick={(e) => selectedCustomer && handleDeleteClick(e, selectedCustomer)} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Customer Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} aria-labelledby="delete-customer-dialog-title" aria-describedby="delete-customer-dialog-description">
        <DialogTitle id="delete-customer-dialog-title">Delete Customer?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-customer-dialog-description">
            Are you sure you want to delete {customerToDelete ? custName(customerToDelete) : 'this customer'}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDeleteCustomer} color="error" variant="contained" autoFocus disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
